/* Crosslander is added only on an explicit click. Shopify owns prices/discounts.
 * Refresh Shopify-rendered sections without intercepting other cart actions.
 */
(() => {
  if (customElements.get('zk-crosslander-upsell')) return;
  const root = window.Shopify?.routes?.root || '/';
  let adding = false;

  async function request(url, body) {
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
    });
    const data = await response.json();
    if (!response.ok) {
      console.warn('Crosslander cart request failed', response.status, new URL(url, window.location.origin).pathname, data);
      throw new Error(data.description || 'Kurven kunne ikke opdateres. Prøv igen.');
    }
    return data;
  }

  class CrosslanderUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('.zk-cl-upsell__add');
      this.error = this.querySelector('.zk-cl-upsell__error');
      this.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"]')) return;
        const selected = event.target;
        const image = this.querySelector('.zk-cl-upsell__image');
        image.src = selected.dataset.image;
        image.alt = 'Crosslander Rygsæk 25L – ' + selected.dataset.colour;
        this.querySelector('[data-selected-colour]').textContent = selected.dataset.colour;
      });
      this.button.addEventListener('click', () => this.addBag());
    }

    async refreshCart(cart, controller) {
      if (!controller?.getSectionsToRender || !controller?.renderContentsAndDependencies) {
        window.location.assign(root + 'cart');
        return;
      }
      const sections = controller.onCartPage ? [...new Set(controller.getSectionsToRender()
        .filter(section => document.getElementById(section.id) || (section.bubble && document.querySelector(section.selector)))
        .map(section => section.section).filter(Boolean))] : ['header-cart', 'cart-icon-bubble'];
      const rendered = {};
      // Shopify section rendering supports at most five sections per request.
      for (let start = 0; start < sections.length; start += 5) {
        const url = new URL(controller.onCartPage ? window.location.href : root, window.location.origin);
        url.searchParams.set('sections', sections.slice(start, start + 5).join(','));
        const result = await request(url.toString());
        if (Object.values(result).some(html => html === null)) throw new Error('Kurven skal genindlæses.');
        Object.assign(rendered, result);
      }
      const state = { ...cart, sections: rendered };
      if (controller.onCartPage) {
        controller.renderContentsAndDependencies(state);
        return;
      }
      // The popup may move/clone its cart element. Use the actual containing
      // element instead of relying on document-wide IDs for drawer fragments.
      const parser = new DOMParser();
      const documents = Object.fromEntries(Object.entries(rendered).map(([id, html]) => [id, parser.parseFromString(html, 'text/html')]));
      const replacements = [];
      if (!documents['header-cart']?.querySelector('.js-contents-cart-items') ||
          !documents['header-cart']?.querySelector('.js-contents-subtotal')) throw new Error('Kurven skal genindlæses.');
      for (const selector of ['.js-contents-cart-items', '.js-contents-subtotal', '.js-contents-buttons', '.js-contents-free-shipping']) {
        const source = documents['header-cart'].querySelector(selector);
        const target = controller.querySelector(selector);
        if (source && target) replacements.push([target, source.innerHTML]);
      }
      const counter = documents['cart-icon-bubble']?.querySelector('.header-cart-count');
      if (counter) document.querySelectorAll('.header-cart-count').forEach(target => replacements.push([target, counter.innerHTML]));
      for (const [target, html] of replacements) target.innerHTML = html;
      controller.toggleEmptyClass(state);
      controller.currentLineCount = controller.querySelectorAll('[name="updates[]"]').length;
      controller.currentItemCount = cart.item_count;
      controller.setParsedState(state);
      controller.setCartJs();
      document.querySelector('shipping-calculator')?.setUpdateStatus('true');
      if (typeof Scrollbar !== 'undefined') {
        controller.querySelectorAll('[data-scrollbar]').forEach(scroll => Scrollbar.get(scroll)?.update());
      }
    }

    async addBag() {
      if (adding) return;
      const selected = this.querySelector('input[type="radio"]:checked');
      if (!selected || selected.disabled) return;
      adding = true;
      this.button.disabled = true;
      this.button.textContent = 'Tilføjer…';
      this.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      const controller = this.closest('cart-items');
      let addAttempted = false;
      let addRejected = false;
      try {
        const before = await request(root + 'cart.js');
        const eligible = before.items.some(item => item.handle === this.dataset.bundleHandle) &&
          !before.items.some(item => String(item.product_id) === this.dataset.bagId);
        if (!eligible) {
          await this.refreshCart(before, controller);
          return;
        }
        addAttempted = true;
        try {
          await request(root + 'cart/add.js', { items: [{ id: Number(selected.value), quantity: 1 }] });
        } catch (error) {
          // Resolve uncertain network outcomes before offering another click.
          const current = await request(root + 'cart.js');
          if (current.items.some(item => String(item.product_id) === this.dataset.bagId)) {
            await this.refreshCart(current, controller);
            return;
          }
          addRejected = true;
          throw error;
        }
        await this.refreshCart(await request(root + 'cart.js'), controller);
      } catch (error) {
        if (addAttempted && !addRejected) {
          console.warn('Crosslander: viser den opdaterede kurvside.', error);
          // Never replay a successful or uncertain add after a rendering failure.
          window.location.assign(root + 'cart');
          return;
        }
        this.error.textContent = error.message || 'Der opstod en fejl. Prøv igen.';
        this.error.hidden = false;
      } finally {
        adding = false;
        this.button.disabled = false;
        this.button.textContent = 'Tilføj til kurv';
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('zk-crosslander-upsell', CrosslanderUpsell);
})();
